import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">QuizMind</h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t('terms.backToHome')}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-6 sm:p-12 shadow-xl"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {t('terms.title')}
          </h1>

          <p className="text-sm text-slate-500 mb-8">
            {t('terms.lastUpdated')}
          </p>

          <div className="prose prose-slate max-w-none">

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section1Title')}
              </h2>
              <p>{t('terms.section1Text')}</p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section2Title')}
              </h2>
              <p>{t('terms.section2Text')}</p>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section3Title')}
              </h2>
              <p className="mb-4">{t('terms.section3Intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{t('terms.section3Point1')}</li>
                <li>{t('terms.section3Point2')}</li>
                <li>{t('terms.section3Point3')}</li>
              </ul>
            </section>

            {/* Section 4 - Refund */}
            <section className="mb-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section4Title')}
              </h2>

              <p className="font-semibold mb-4">
                {t('terms.section4Important')}
              </p>

              <p className="mb-4">{t('terms.section4Intro')}</p>

              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>{t('terms.section4Point1')}</li>
                <li>{t('terms.section4Point2')}</li>
                <li>{t('terms.section4Point3')}</li>
                <li>{t('terms.section4Point4')}</li>
              </ul>

              <p>{t('terms.section4Outro')}</p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section5Title')}
              </h2>
              <p>{t('terms.section5Text')}</p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section6Title')}
              </h2>
              <p>{t('terms.section6Text')}</p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section7Title')}
              </h2>
              <p>{t('terms.section7Text')}</p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section8Title')}
              </h2>
              <p>{t('terms.section8Text')}</p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section9Title')}
              </h2>
              <p>{t('terms.section9Text')}</p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section10Title')}
              </h2>
              <p>{t('terms.section10Text')}</p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t('terms.section11Title')}
              </h2>
              <p className="mb-2">{t('terms.section11Intro')}</p>
              <p>
                <a
                  href="mailto:support@quizmind.com"
                  className="text-violet-600 hover:text-violet-700 underline"
                >
                  {t('terms.section11Email')}
                </a>
              </p>
            </section>

            {/* Bottom Notice */}
            <div className="mt-12 p-6 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600 text-center">
                {t('terms.footerNote')}
              </p>
            </div>

          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm border-t border-slate-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-slate-600">QuizMind</span>
            </div>
            <p className="text-sm text-slate-600">
              {t('terms.footerCopyright')}
            </p>

          </div>
        </div>
      </footer>
    </div>
  );
}
